/**
 * Access Financials GraphQL Query Templates
 *
 * Pre-built templates for common operations with pagination support.
 * Templates are organized by Domain > Resource > Action
 *
 * Schema structure:
 * - domain.lists.xxx - Summary/list queries with pagination
 * - domain.masterRecords.xxx.records - Full record queries with pagination
 * - domain.transactions.xxx - Transaction queries
 */

export interface QueryTemplate {
  name: string;
  description: string;
  query: string;
  variables?: string;
}

export interface ResourceDefinition {
  name: string;
  description: string;
  actions: {
    [action: string]: QueryTemplate;
  };
}

export interface DomainDefinition {
  name: string;
  description: string;
  resources: {
    [resource: string]: ResourceDefinition;
  };
}

/**
 * Domain definitions with their resources and actions
 */
export const domains: { [domain: string]: DomainDefinition } = {
  // ============================================================
  // DEBTORS (Customers)
  // ============================================================
  debtors: {
    name: 'Debtors',
    description: 'Customer management and accounts receivable',
    resources: {
      customers: {
        name: 'Customers',
        description: 'Customer records',
        actions: {
          list: {
            name: 'List Customers (Summary)',
            description: 'Get a paginated summary list of customers',
            query: `query ListCustomers($first: Int, $after: String, $filter: CustomerSummaryFilterInput) {
  debtors {
    lists {
      customers(first: $first, after: $after, filter: $filter) {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        items {
          primaryKey
          code
          name
          onStop
          balance
          creditLimit
          availableCreditLimit
          currencyCode
          postCode
          country
          inactive
        }
      }
    }
  }
}`,
            variables: `{
  "first": 50,
  "after": null,
  "filter": null
}`,
          },
          listFull: {
            name: 'List Customers (Full Details)',
            description: 'Get full customer records with all details',
            query: `query ListCustomersFull($first: Int, $after: String, $filter: CustomerFilterInput) {
  debtors {
    masterRecords {
      customers {
        records(first: $first, after: $after, filter: $filter) {
          pageInfo {
            hasNextPage
            endCursor
          }
          items {
            customerDetails {
              primaryKey
              uniqueId
              code
              name
              onStop
              notes
              contactDetails {
                title
                firstName
                surname
                jobTitle
              }
              addressDetails {
                address
                town
                county
                postCode
                country
                telephone
                email
                fax
                mobile
              }
            }
            terms {
              priceKey
              terms
            }
          }
        }
      }
    }
  }
}`,
            variables: `{
  "first": 50,
  "after": null,
  "filter": null
}`,
          },
          getByCode: {
            name: 'Get Customer by Code',
            description: 'Retrieve a specific customer by their code',
            query: `query GetCustomer($code: String!) {
  debtors {
    lists {
      customers(first: 1, filter: { code: { eq: $code } }) {
        items {
          primaryKey
          code
          name
          onStop
          balance
          creditLimit
          currencyCode
          postCode
          country
        }
      }
    }
  }
}`,
            variables: `{
  "code": "CUSTOMER_CODE"
}`,
          },
          agedDebt: {
            name: 'Customer Aged Debt',
            description: 'Get aged debt information for a customer',
            query: `query CustomerAgedDebt($customerCode: String!) {
  debtors {
    masterRecords {
      customers {
        agedDebt(customerCode: $customerCode) {
          range1
          range2
          range3
          range4
          range5
          range6
          total
        }
      }
    }
  }
}`,
            variables: `{
  "customerCode": "CUSTOMER_CODE"
}`,
          },
          create: {
            name: 'Create Customer',
            description: 'Create a new customer record',
            query: `mutation CreateCustomer($customer: CustomerInput!) {
  debtors {
    masterRecords {
      customer {
        create(customer: $customer) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "customer": {
    "customerDetails": {
      "code": "NEW001",
      "name": "New Customer Ltd",
      "contactDetails": {
        "email": "contact@newcustomer.com",
        "telephone": "0123456789"
      },
      "addressDetails": {
        "address": "123 Main Street",
        "town": "London",
        "postCode": "SW1A 1AA",
        "country": "United Kingdom"
      }
    },
    "terms": {
      "credit": {
        "creditLimit": 10000
      }
    }
  }
}`,
          },
          update: {
            name: 'Update Customer',
            description: 'Update an existing customer record',
            query: `mutation UpdateCustomer($customer: CustomerInput!) {
  debtors {
    masterRecords {
      customer {
        update(customer: $customer) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "customer": {
    "customerDetails": {
      "primaryKey": 123,
      "name": "Updated Customer Name",
      "contactDetails": {
        "email": "updated@customer.com"
      }
    }
  }
}`,
          },
          delete: {
            name: 'Delete Customer',
            description: 'Delete one or more customer records',
            query: `mutation DeleteCustomer($primaryKeys: [Int!]) {
  debtors {
    masterRecords {
      customer {
        delete(primaryKeys: $primaryKeys) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "primaryKeys": [123]
}`,
          },
          updateOnStop: {
            name: 'Update Customer On Stop',
            description: 'Toggle a customer\u0027s on-stop status',
            query: `mutation UpdateCustomerOnStop($primaryKey: Int!) {
  debtors {
    masterRecords {
      customer {
        updateCustomerOnStop(primaryKey: $primaryKey) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "primaryKey": 123
}`,
          },
        },
      },
      customerAddresses: {
        name: 'Customer Addresses',
        description: 'Customer delivery and invoice addresses',
        actions: {
          list: {
            name: 'List Customer Addresses',
            description: 'Get address summary for customers',
            query: `query ListCustomerAddresses($first: Int, $after: String, $filter: CustomerAddressSummaryFilterInput) {
  debtors {
    lists {
      customerAddressSummary(first: $first, after: $after, filter: $filter) {
        pageInfo {
          hasNextPage
          endCursor
        }
        items {
          primaryKey
          number
          customerCode
          isInvoice
          isStatement
          isDelivery
          address
          town
          county
          postCode
          country
          telephone
          mobile
          fax
          email
        }
      }
    }
  }
}`,
            variables: `{
  "first": 50,
  "after": null,
  "filter": null
}`,
          },
        },
      },
      salesInvoices: {
        name: 'Sales Invoices',
        description: 'Sales invoice transactions',
        actions: {
          create: {
            name: 'Create Sales Invoice',
            description: 'Create a new sales invoice',
            query: `mutation CreateSalesInvoice($invoice: SalesTransactionInput!, $validateOnly: Boolean) {
  debtors {
    transactions {
      invoice {
        create(invoice: $invoice, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message details { primaryKey field value } }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "invoice": {
    "header": {
      "customer": { "code": "CUST001" },
      "headerReference": "INV-001",
      "description": "Sales Invoice",
      "dates": { "date": "2024-01-15", "dueDate": "2024-02-15" },
      "period": { "year": "Current" },
      "currency": { "symbol": "£" }
    },
    "options": {
      "batched": false,
      "transactionValuesEntry": "HomeNet"
    },
    "detailLines": [
      {
        "action": "Insert",
        "detail": "Product/Service Description",
        "analysis": { "code": "SALES" },
        "tax": { "code": "T1" },
        "values": { "net": 100.00, "tax": 20.00, "gross": 120.00 }
      }
    ]
  }
}`,
          },
          update: {
            name: 'Update Sales Invoice',
            description: 'Update an existing sales invoice',
            query: `mutation UpdateSalesInvoice($invoice: SalesTransactionInput!, $validateOnly: Boolean) {
  debtors {
    transactions {
      invoice {
        update(invoice: $invoice, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message details { primaryKey field value } }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "invoice": {
    "header": {
      "primaryKey": 123,
      "description": "Updated Description"
    }
  }
}`,
          },
        },
      },
      salesCreditNotes: {
        name: 'Sales Credit Notes',
        description: 'Sales credit note transactions',
        actions: {
          create: {
            name: 'Create Sales Credit Note',
            description: 'Create a new sales credit note',
            query: `mutation CreateSalesCreditNote($creditNote: SalesTransactionInput!, $validateOnly: Boolean) {
  debtors {
    transactions {
      creditNote {
        create(creditNote: $creditNote, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message details { primaryKey field value } }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "creditNote": {
    "header": {
      "customer": { "code": "CUST001" },
      "headerReference": "CN-001",
      "description": "Credit Note",
      "dates": { "date": "2024-01-15" },
      "period": { "year": "Current" }
    },
    "detailLines": [
      {
        "action": "Insert",
        "detail": "Credit for returned goods",
        "analysis": { "code": "SALES" },
        "tax": { "code": "T1" },
        "values": { "net": 50.00, "tax": 10.00, "gross": 60.00 }
      }
    ]
  }
}`,
          },
          update: {
            name: 'Update Sales Credit Note',
            description: 'Update an existing sales credit note',
            query: `mutation UpdateSalesCreditNote($creditNote: SalesTransactionInput!, $validateOnly: Boolean) {
  debtors {
    transactions {
      creditNote {
        update(creditNote: $creditNote, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "creditNote": {
    "header": { "primaryKey": 123 }
  }
}`,
          },
        },
      },
      salesReceipts: {
        name: 'Sales Receipts',
        description: 'Customer payment receipts',
        actions: {
          create: {
            name: 'Create Sales Receipt',
            description: 'Record a customer payment receipt',
            query: `mutation CreateSalesReceipt($receipt: SalesTransactionInput!, $validateOnly: Boolean) {
  debtors {
    transactions {
      receipt {
        create(receipt: $receipt, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message details { primaryKey field value } }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "receipt": {
    "header": {
      "customer": { "code": "CUST001" },
      "headerReference": "REC-001",
      "description": "Payment Receipt",
      "dates": { "receiptDate": "2024-01-20" },
      "period": { "year": "Current" }
    },
    "detailLines": [
      {
        "action": "Insert",
        "detail": "Payment received",
        "analysis": { "code": "BANK" },
        "values": { "net": 120.00 }
      }
    ]
  }
}`,
          },
          update: {
            name: 'Update Sales Receipt',
            description: 'Update an existing sales receipt',
            query: `mutation UpdateSalesReceipt($receipt: SalesTransactionInput!, $validateOnly: Boolean) {
  debtors {
    transactions {
      receipt {
        update(receipt: $receipt, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "receipt": {
    "header": { "primaryKey": 123 }
  }
}`,
          },
        },
      },
      salesDebitAdjustment: {
        name: 'Sales Debit Adjustments',
        description: 'Debit adjustment transactions',
        actions: {
          create: {
            name: 'Create Debit Adjustment',
            description: 'Create a sales debit adjustment',
            query: `mutation CreateSalesDebitAdjustment($debitAdjustment: SalesTransactionInput!, $validateOnly: Boolean) {
  debtors {
    transactions {
      debitAdjustment {
        create(debitAdjustment: $debitAdjustment, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "debitAdjustment": {
    "header": {
      "customer": { "code": "CUST001" },
      "headerReference": "DA-001",
      "description": "Debit Adjustment",
      "dates": { "date": "2024-01-15" },
      "period": { "year": "Current" }
    },
    "detailLines": [
      {
        "action": "Insert",
        "detail": "Adjustment description",
        "analysis": { "code": "ADJ" },
        "values": { "net": 25.00 }
      }
    ]
  }
}`,
          },
          update: {
            name: 'Update Debit Adjustment',
            description: 'Update an existing debit adjustment',
            query: `mutation UpdateSalesDebitAdjustment($debitAdjustment: SalesTransactionInput!, $validateOnly: Boolean) {
  debtors {
    transactions {
      debitAdjustment {
        update(debitAdjustment: $debitAdjustment, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "debitAdjustment": {
    "header": { "primaryKey": 123 }
  }
}`,
          },
        },
      },
      salesCreditAdjustment: {
        name: 'Sales Credit Adjustments',
        description: 'Credit adjustment transactions',
        actions: {
          create: {
            name: 'Create Credit Adjustment',
            description: 'Create a sales credit adjustment',
            query: `mutation CreateSalesCreditAdjustment($creditAdjustment: SalesTransactionInput!, $validateOnly: Boolean) {
  debtors {
    transactions {
      creditAdjustment {
        create(creditAdjustment: $creditAdjustment, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "creditAdjustment": {
    "header": {
      "customer": { "code": "CUST001" },
      "headerReference": "CA-001",
      "description": "Credit Adjustment",
      "dates": { "date": "2024-01-15" },
      "period": { "year": "Current" }
    },
    "detailLines": [
      {
        "action": "Insert",
        "detail": "Adjustment description",
        "analysis": { "code": "ADJ" },
        "values": { "net": 25.00 }
      }
    ]
  }
}`,
          },
          update: {
            name: 'Update Credit Adjustment',
            description: 'Update an existing credit adjustment',
            query: `mutation UpdateSalesCreditAdjustment($creditAdjustment: SalesTransactionInput!, $validateOnly: Boolean) {
  debtors {
    transactions {
      creditAdjustment {
        update(creditAdjustment: $creditAdjustment, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "creditAdjustment": {
    "header": { "primaryKey": 123 }
  }
}`,
          },
        },
      },
      salesCashAllocation: {
        name: 'Sales Cash Allocation',
        description: 'Allocate receipts to invoices',
        actions: {
          process: {
            name: 'Process Cash Allocation',
            description: 'Allocate receipt amounts to outstanding invoices',
            query: `mutation ProcessSalesCashAllocation($data: CashAllocationProcessInput!) {
  debtors {
    transactions {
      cashAllocation {
        processAllocations(data: $data) {
          ... on CashAllocationResult {
            allocationRef
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "data": {
    "date": "2024-01-20",
    "period": { "year": "Current", "number": 1 },
    "transactions": [
      { "primaryKey": 123, "amount": 120.00 }
    ]
  }
}`,
          },
          reverse: {
            name: 'Reverse Cash Allocation',
            description: 'Reverse a previous cash allocation',
            query: `mutation ReverseSalesCashAllocation($data: CashAllocationProcessInput!) {
  debtors {
    transactions {
      reverseCashAllocation {
        processAllocationReversals(data: $data) {
          ... on CashAllocationResult {
            allocationRef
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "data": {
    "date": "2024-01-20",
    "period": { "year": "Current", "number": 1 },
    "transactions": [
      { "primaryKey": 123, "amount": 120.00 }
    ]
  }
}`,
          },
        },
      },
      salesBulkTransactions: {
        name: 'Sales Bulk Transactions',
        description: 'Bulk create/update multiple sales transactions',
        actions: {
          bulkCreate: {
            name: 'Bulk Create Sales Transactions',
            description: 'Create multiple sales transactions in one call',
            query: `mutation BulkCreateSalesTransactions($transactions: [SalesTransactionBulkInput!]!, $validateOnly: Boolean) {
  debtors {
    transactions {
      bulkTransactions {
        bulkCreate(transactions: $transactions, validateOnly: $validateOnly) {
          ... on IntEntityIds {
            ids
          }
          ... on FailedValidation {
            errors { message details { primaryKey field value } }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "transactions": [
    {
      "transactionType": "Invoice",
      "index": "1",
      "header": {
        "customer": { "code": "CUST001" },
        "headerReference": "BULK-001",
        "dates": { "date": "2024-01-15" },
        "period": { "year": "Current" }
      },
      "detailLines": [
        {
          "action": "Insert",
          "detail": "Item 1",
          "analysis": { "code": "SALES" },
          "values": { "net": 100.00 }
        }
      ]
    }
  ]
}`,
          },
          bulkUpdate: {
            name: 'Bulk Update Sales Transactions',
            description: 'Update multiple sales transactions in one call',
            query: `mutation BulkUpdateSalesTransactions($transactions: [SalesTransactionBulkInput!]!, $validateOnly: Boolean) {
  debtors {
    transactions {
      bulkTransactions {
        bulkUpdate(transactions: $transactions, validateOnly: $validateOnly) {
          ... on IntEntityIds {
            ids
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "transactions": [
    {
      "transactionType": "Invoice",
      "header": { "primaryKey": 123 }
    }
  ]
}`,
          },
        },
      },
      salesBatchedTransactions: {
        name: 'Sales Batched Transactions',
        description: 'Manage batched sales transactions',
        actions: {
          post: {
            name: 'Post Batched Transactions',
            description: 'Post sales transactions from batch to live',
            query: `mutation PostSalesBatchedTransactions($primaryKeys: [Int!]!, $postToRestrictedPeriod: Boolean) {
  debtors {
    transactions {
      batchedTransactions {
        post(primaryKeys: $primaryKeys, postToRestrictedPeriod: $postToRestrictedPeriod) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "primaryKeys": [123, 456],
  "postToRestrictedPeriod": false
}`,
          },
          delete: {
            name: 'Delete Batched Transactions',
            description: 'Delete batched sales transactions',
            query: `mutation DeleteSalesBatchedTransactions($primaryKeys: [Int!]!) {
  debtors {
    transactions {
      batchedTransactions {
        delete(primaryKeys: $primaryKeys) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "primaryKeys": [123]
}`,
          },
          lock: {
            name: 'Lock Batched Transactions',
            description: 'Lock transactions for editing',
            query: `mutation LockSalesBatchedTransactions($primaryKeys: [Int!]!) {
  debtors {
    transactions {
      batchedTransactions {
        lock(primaryKeys: $primaryKeys) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "primaryKeys": [123]
}`,
          },
          unlock: {
            name: 'Unlock Batched Transactions',
            description: 'Release locks on transactions',
            query: `mutation UnlockSalesBatchedTransactions($primaryKeys: [Int!]!) {
  debtors {
    transactions {
      batchedTransactions {
        unlock(primaryKeys: $primaryKeys) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "primaryKeys": [123]
}`,
          },
        },
      },
      badDebts: {
        name: 'Bad Debts',
        description: 'Write off and reverse bad debts',
        actions: {
          writeOff: {
            name: 'Write Off Bad Debts',
            description: 'Write off customer bad debts',
            query: `mutation WriteOffBadDebts($badDebts: ProcessBadDebtsInput!) {
  debtors {
    transactions {
      badDebts {
        writeOff(badDebts: $badDebts) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "badDebts": {
    "transactionDate": "2024-01-15",
    "currencyCode": "GBP",
    "analysisCode": "BADDEBT",
    "allocationPeriod": 1,
    "allocationYear": "Current",
    "primaryKeys": [123, 456]
  }
}`,
          },
          reverse: {
            name: 'Reverse Bad Debts',
            description: 'Reverse previously written off bad debts',
            query: `mutation ReverseBadDebts($badDebts: ProcessBadDebtsInput!) {
  debtors {
    transactions {
      badDebts {
        reverse(badDebts: $badDebts) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "badDebts": {
    "transactionDate": "2024-01-15",
    "currencyCode": "GBP",
    "analysisCode": "BADDEBT",
    "allocationPeriod": 1,
    "allocationYear": "Current",
    "primaryKeys": [123]
  }
}`,
          },
        },
      },
      bulkReceipts: {
        name: 'Bulk Receipts',
        description: 'Process bulk customer receipts',
        actions: {
          validate: {
            name: 'Validate Bulk Receipts',
            description: 'Validate and prepare bulk receipts before processing',
            query: `mutation ValidateBulkReceipts($data: BulkReceiptValidationInput!) {
  debtors {
    transactions {
      bulkReceipt {
        validate(data: $data) {
          ... on BulkReceiptProcessOptions {
            batchId
            homeDifference
            differenceType
            allocationDate
            receiptReference
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "data": {
    "currencySymbol": "£",
    "receiptDate": "2024-01-20",
    "receiptYear": "Current",
    "receiptPeriod": 1,
    "allocationDate": "2024-01-20",
    "allocationYear": "Current",
    "allocationPeriod": 1,
    "bankAnalysis": "BANK",
    "transactions": [
      {
        "accountCode": "CUST001",
        "amountToAllocate": 500.00,
        "settlementValue": 500.00
      }
    ]
  }
}`,
          },
          process: {
            name: 'Process Bulk Receipts',
            description: 'Process bulk receipts after validation',
            query: `mutation ProcessBulkReceipts($data: BulkReceiptProcessOptionsInput!) {
  debtors {
    transactions {
      bulkReceipt {
        process(data: $data) {
          ... on BulkReceiptResult {
            bulkReceiptProcessOptions {
              batchId
            }
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "data": {
    "batchId": 123,
    "allocationDate": "2024-01-20",
    "allocationYear": "Current",
    "allocationPeriod": 1,
    "receiptDate": "2024-01-20",
    "receiptYear": "Current",
    "receiptPeriod": 1,
    "bankAnalysis": "BANK"
  }
}`,
          },
        },
      },
      directDebit: {
        name: 'Direct Debit',
        description: 'Direct debit collections management',
        actions: {
          processCollections: {
            name: 'Process DD Collections',
            description: 'Process direct debit collections',
            query: `mutation ProcessDirectDebitCollections($collection: DirectDebitCollectionInput!) {
  debtors {
    transactions {
      directDebit {
        processCollections(collection: $collection) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "collection": {
    "reference": "DD-001",
    "bankAnalysis": "BANK",
    "currency": "GBP",
    "receiptDate": "2024-01-20",
    "receiptYear": "Current",
    "receiptPeriod": 1,
    "allocationDate": "2024-01-20",
    "allocationYear": "Current",
    "allocationPeriod": 1,
    "collections": [
      {
        "auditNumber": 123,
        "customer": "CUST001",
        "allocated": 100.00
      }
    ]
  }
}`,
          },
          completeCollections: {
            name: 'Complete DD Collections',
            description: 'Complete direct debit collections export',
            query: `mutation CompleteDirectDebitCollections($collection: DirectDebitCompleteExportInput!) {
  debtors {
    transactions {
      directDebit {
        completeCollections(collection: $collection) {
          ... on Succeeded {
            succeeded
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "collection": {
    "paymentDebitDate": "2024-01-25",
    "receiptReference": "DD-BATCH-001",
    "collectionsTransactionsPrimaryKeys": [123, 456]
  }
}`,
          },
        },
      },
      salesTransactionActions: {
        name: 'Sales Transaction Actions',
        description: 'Common transaction actions',
        actions: {
          dispute: {
            name: 'Dispute Transaction',
            description: 'Link a query reason to a sales transaction',
            query: `mutation DisputeSalesTransaction($transactionDisputeReason: TransactionDisputeReasonInput!) {
  debtors {
    transactions {
      transactionActions {
        dispute(transactionDisputeReason: $transactionDisputeReason) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "transactionDisputeReason": {
    "transactionPrimaryKey": 123,
    "queryReasonPrimaryKey": 1,
    "queryFlag": true,
    "note": "Customer dispute - incorrect pricing"
  }
}`,
          },
          delete: {
            name: 'Delete Transaction',
            description: 'Delete a sales transaction',
            query: `mutation DeleteSalesTransaction($primaryKey: Int!) {
  debtors {
    transactions {
      transactionActions {
        delete(primaryKey: $primaryKey) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "primaryKey": 123
}`,
          },
        },
      },
      creditControlNotes: {
        name: 'Credit Control Notes',
        description: 'Credit control notes for customers',
        actions: {
          create: {
            name: 'Create Credit Control Note',
            description: 'Create a new credit control note',
            query: `mutation CreateCreditControlNote($note: CreditControlNoteInput!) {
  debtors {
    transactions {
      creditControlNote {
        create(note: $note) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "note": {
    "primaryKey": 0,
    "customerCode": "CUST001",
    "note": "Called customer - promised payment by end of week",
    "nextContactDate": "2024-01-22",
    "paymentDate": "2024-01-26",
    "paymentValue": 500.00,
    "transactionPrimaryKeys": [123, 456]
  }
}`,
          },
          update: {
            name: 'Update Credit Control Note',
            description: 'Update an existing credit control note',
            query: `mutation UpdateCreditControlNote($note: CreditControlNoteInput!) {
  debtors {
    transactions {
      creditControlNote {
        update(note: $note) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "note": {
    "primaryKey": 789,
    "note": "Updated note - payment received",
    "paymentDate": "2024-01-25"
  }
}`,
          },
        },
      },
      chaseDebtors: {
        name: 'Chase Debtors',
        description: 'Customer statements and chasing',
        actions: {
          processStatements: {
            name: 'Process Statements',
            description: 'Generate and send customer statements',
            query: `mutation ProcessCustomerStatements($options: CustomerStatementOptionsInput!) {
  debtors {
    masterRecords {
      chaseDebtors {
        processStatements(options: $options) {
          ... on ProcessedStatementsResult {
            processedTransactionCount
            batchIds
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "options": {
    "agedDate": "2024-01-31",
    "anyCurrency": false,
    "balanceType": "TransactionBalance",
    "currencyMode": "Home",
    "includeFullyAllocatedTransactions": false,
    "periodNumber": 1,
    "statementProcessingType": "Email",
    "year": "Current",
    "customerCodes": ["CUST001", "CUST002"]
  }
}`,
          },
        },
      },
      salesAnalysis: {
        name: 'Sales Analysis',
        description: 'Sales analysis codes',
        actions: {
          create: {
            name: 'Create Sales Analysis',
            description: 'Create a new sales analysis code',
            query: `mutation CreateSalesAnalysis($salesAnalysis: SalesAnalysisInput!) {
  debtors {
    masterRecords {
      analyses {
        create(salesAnalysis: $salesAnalysis) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "salesAnalysis": {
    "code": "SA001",
    "name": "General Sales",
    "analysisType": "Sales",
    "isInactive": false,
    "taxCode": "T1",
    "debit": "4000",
    "credit": "1100"
  }
}`,
          },
          update: {
            name: 'Update Sales Analysis',
            description: 'Update an existing sales analysis code',
            query: `mutation UpdateSalesAnalysis($salesAnalysis: SalesAnalysisInput!) {
  debtors {
    masterRecords {
      analyses {
        update(salesAnalysis: $salesAnalysis) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "salesAnalysis": {
    "primaryKey": 123,
    "name": "Updated Sales Analysis"
  }
}`,
          },
          delete: {
            name: 'Delete Sales Analysis',
            description: 'Delete sales analysis codes',
            query: `mutation DeleteSalesAnalysis($salesAnalyses: [DeleteRecordItemInput!]!, $validateOnly: Boolean) {
  debtors {
    masterRecords {
      analyses {
        delete(salesAnalyses: $salesAnalyses, validateOnly: $validateOnly) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "salesAnalyses": [{ "code": "SA001" }]
}`,
          },
        },
      },
      termsTemplates: {
        name: 'Terms Templates',
        description: 'Customer terms templates',
        actions: {
          create: {
            name: 'Create Terms Template',
            description: 'Create a new terms template',
            query: `mutation CreateTermsTemplate($termsTemplate: TermsTemplateInput!) {
  debtors {
    masterRecords {
      terms {
        create(termsTemplate: $termsTemplate) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "termsTemplate": {
    "code": "STANDARD",
    "description": "Standard 30 day terms",
    "isDefault": false,
    "dueDays": 30,
    "dueType": "DaysFromInvoice",
    "creditLimit": 5000.00
  }
}`,
          },
          update: {
            name: 'Update Terms Template',
            description: 'Update an existing terms template',
            query: `mutation UpdateTermsTemplate($termsTemplate: TermsTemplateInput!) {
  debtors {
    masterRecords {
      terms {
        update(termsTemplate: $termsTemplate) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "termsTemplate": {
    "primaryKey": 123,
    "description": "Updated terms"
  }
}`,
          },
          delete: {
            name: 'Delete Terms Templates',
            description: 'Delete terms templates',
            query: `mutation DeleteTermsTemplates($termsTemplates: [DeleteTermsTemplateInput!]!) {
  debtors {
    masterRecords {
      terms {
        delete(termsTemplates: $termsTemplates) {
          ... on IntEntityIds {
            ids
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "termsTemplates": [{ "primaryKey": 123 }]
}`,
          },
        },
      },
    },
  },

  // ============================================================
  // CREDITORS (Suppliers)
  // ============================================================
  creditors: {
    name: 'Creditors',
    description: 'Supplier management and accounts payable',
    resources: {
      suppliers: {
        name: 'Suppliers',
        description: 'Supplier records',
        actions: {
          list: {
            name: 'List Suppliers (Summary)',
            description: 'Get a paginated summary list of suppliers',
            query: `query ListSuppliers($first: Int, $after: String, $filter: SupplierSummaryFilterInput) {
  creditors {
    lists {
      suppliers(first: $first, after: $after, filter: $filter) {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        items {
          primaryKey
          code
          name
          onStop
          balance
          creditLimit
          currencyCode
          currencySymbol
          sortKey
          inactive
        }
      }
    }
  }
}`,
            variables: `{
  "first": 50,
  "after": null,
  "filter": null
}`,
          },
          listFull: {
            name: 'List Suppliers (Full Details)',
            description: 'Get full supplier records with all details',
            query: `query ListSuppliersFull($first: Int, $after: String, $filter: SupplierFilterInput) {
  creditors {
    masterRecords {
      suppliers {
        records(first: $first, after: $after, filter: $filter) {
          pageInfo {
            hasNextPage
            endCursor
          }
          items {
          supplierDetails {
            primaryKey
            uniqueId
            code
            name
            onStop
            notes
            contactDetails {
              title
              firstName
              surname
              jobTitle
            }
            addressDetails {
              address
              town
              county
              postCode
              country
              telephone
              email
              fax
              mobile
            }
          }
          terms {
            priceKey
            terms
          }
          }
        }
      }
    }
  }
}`,
            variables: `{
  "first": 50,
  "after": null,
  "filter": null
}`,
          },
          getByCode: {
            name: 'Get Supplier by Code',
            description: 'Retrieve a specific supplier by their code',
            query: `query GetSupplier($code: String!) {
  creditors {
    lists {
      suppliers(first: 1, filter: { code: { eq: $code } }) {
        items {
          primaryKey
          code
          name
          onStop
          balance
          creditLimit
          currencyCode
          currencySymbol
          inactive
        }
      }
    }
  }
}`,
            variables: `{
  "code": "SUPPLIER_CODE"
}`,
          },
          agedCredit: {
            name: 'Supplier Aged Credit',
            description: 'Get aged credit information for a supplier',
            query: `query SupplierAgedCredit($supplierCode: String!) {
  creditors {
    masterRecords {
      suppliers {
        agedCredit(supplierCode: $supplierCode) {
          range1
          range2
          range3
          range4
          range5
          range6
          total
        }
      }
    }
  }
}`,
            variables: `{
  "supplierCode": "SUPPLIER_CODE"
}`,
          },
          create: {
            name: 'Create Supplier',
            description: 'Create a new supplier record',
            query: `mutation CreateSupplier($supplier: SupplierInput!, $enableDetailedValidation: Boolean) {
  creditors {
    masterRecords {
      supplier {
        create(supplier: $supplier, enableDetailedValidation: $enableDetailedValidation) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message details { primaryKey field value } }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "enableDetailedValidation": false,
  "supplier": {
    "supplierDetails": {
      "code": "SUP001",
      "name": "New Supplier Ltd",
      "contactDetails": {
        "email": "contact@newsupplier.com",
        "telephone": "0123456789"
      },
      "addressDetails": {
        "address": "456 Business Park",
        "town": "Manchester",
        "postCode": "M1 1AA",
        "country": "United Kingdom"
      }
    },
    "terms": {
      "credit": { "creditLimit": 5000 }
    },
    "settings": {
      "currency": { "code": "GBP" }
    }
  }
}`,
          },
          update: {
            name: 'Update Supplier',
            description: 'Update an existing supplier record',
            query: `mutation UpdateSupplier($supplier: SupplierInput!, $enableDetailedValidation: Boolean) {
  creditors {
    masterRecords {
      supplier {
        update(supplier: $supplier, enableDetailedValidation: $enableDetailedValidation) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message details { primaryKey field value } }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "enableDetailedValidation": false,
  "supplier": {
    "supplierDetails": {
      "primaryKey": 123,
      "name": "Updated Supplier Name"
    }
  }
}`,
          },
          delete: {
            name: 'Delete Supplier',
            description: 'Delete one or more supplier records',
            query: `mutation DeleteSupplier($primaryKeys: [Int!]) {
  creditors {
    masterRecords {
      supplier {
        delete(primaryKeys: $primaryKeys) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "primaryKeys": [123]
}`,
          },
          updateNote: {
            name: 'Update Supplier Notes',
            description: 'Update supplier notes',
            query: `mutation UpdateSupplierNote($supplier: RecordNoteInput!) {
  creditors {
    masterRecords {
      supplier {
        updateNote(supplier: $supplier) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "supplier": {
    "primaryKey": 123,
    "code": "SUP001",
    "notes": "Updated supplier notes"
  }
}`,
          },
        },
      },
      purchaseInvoices: {
        name: 'Purchase Invoices',
        description: 'Purchase invoice transactions',
        actions: {
          create: {
            name: 'Create Purchase Invoice',
            description: 'Create a new purchase invoice',
            query: `mutation CreatePurchaseInvoice($invoice: PurchaseTransactionInput!, $validateOnly: Boolean) {
  creditors {
    transactions {
      invoice {
        create(invoice: $invoice, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message details { primaryKey field value } }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "invoice": {
    "header": {
      "supplier": { "code": "SUP001" },
      "headerReference": "PINV-001",
      "description": "Purchase Invoice",
      "dates": { "date": "2024-01-15", "dueDate": "2024-02-15" },
      "period": { "year": "Current" },
      "currency": { "symbol": "£" }
    },
    "options": {
      "batched": false,
      "transactionValuesEntry": "HomeNet"
    },
    "detailLines": [
      {
        "action": "Insert",
        "detail": "Goods/Services received",
        "analysis": { "code": "PURCH" },
        "tax": { "code": "T1" },
        "values": { "net": 500.00, "tax": 100.00, "gross": 600.00 }
      }
    ]
  }
}`,
          },
          update: {
            name: 'Update Purchase Invoice',
            description: 'Update an existing purchase invoice',
            query: `mutation UpdatePurchaseInvoice($invoice: PurchaseTransactionInput!, $validateOnly: Boolean) {
  creditors {
    transactions {
      invoice {
        update(invoice: $invoice, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "invoice": {
    "header": { "primaryKey": 123 }
  }
}`,
          },
        },
      },
      purchaseCreditNotes: {
        name: 'Purchase Credit Notes',
        description: 'Purchase credit note transactions',
        actions: {
          create: {
            name: 'Create Purchase Credit Note',
            description: 'Create a new purchase credit note',
            query: `mutation CreatePurchaseCreditNote($creditNote: PurchaseTransactionInput!, $validateOnly: Boolean) {
  creditors {
    transactions {
      creditNote {
        create(creditNote: $creditNote, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message details { primaryKey field value } }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "creditNote": {
    "header": {
      "supplier": { "code": "SUP001" },
      "headerReference": "PCN-001",
      "description": "Purchase Credit Note",
      "dates": { "date": "2024-01-15" },
      "period": { "year": "Current" }
    },
    "detailLines": [
      {
        "action": "Insert",
        "detail": "Credit for returned goods",
        "analysis": { "code": "PURCH" },
        "tax": { "code": "T1" },
        "values": { "net": 100.00, "tax": 20.00, "gross": 120.00 }
      }
    ]
  }
}`,
          },
          update: {
            name: 'Update Purchase Credit Note',
            description: 'Update an existing purchase credit note',
            query: `mutation UpdatePurchaseCreditNote($creditNote: PurchaseTransactionInput!, $validateOnly: Boolean) {
  creditors {
    transactions {
      creditNote {
        update(creditNote: $creditNote, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "creditNote": {
    "header": { "primaryKey": 123 }
  }
}`,
          },
        },
      },
      purchasePayments: {
        name: 'Purchase Payments',
        description: 'Supplier payment transactions',
        actions: {
          create: {
            name: 'Create Purchase Payment',
            description: 'Record a supplier payment',
            query: `mutation CreatePurchasePayment($payment: PurchaseTransactionInput!, $validateOnly: Boolean) {
  creditors {
    transactions {
      payment {
        create(payment: $payment, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message details { primaryKey field value } }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "payment": {
    "header": {
      "supplier": { "code": "SUP001" },
      "headerReference": "PAY-001",
      "description": "Supplier Payment",
      "dates": { "date": "2024-01-20" },
      "period": { "year": "Current" }
    },
    "detailLines": [
      {
        "action": "Insert",
        "detail": "Payment",
        "analysis": { "code": "BANK" },
        "values": { "net": 600.00 }
      }
    ]
  }
}`,
          },
          update: {
            name: 'Update Purchase Payment',
            description: 'Update an existing purchase payment',
            query: `mutation UpdatePurchasePayment($payment: PurchaseTransactionInput!, $validateOnly: Boolean) {
  creditors {
    transactions {
      payment {
        update(payment: $payment, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "payment": {
    "header": { "primaryKey": 123 }
  }
}`,
          },
        },
      },
      purchaseDebitAdjustment: {
        name: 'Purchase Debit Adjustments',
        description: 'Purchase debit adjustment transactions',
        actions: {
          create: {
            name: 'Create Debit Adjustment',
            description: 'Create a purchase debit adjustment',
            query: `mutation CreatePurchaseDebitAdjustment($debitAdjustment: PurchaseTransactionInput!, $validateOnly: Boolean) {
  creditors {
    transactions {
      debitAdjustment {
        create(debitAdjustment: $debitAdjustment, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "debitAdjustment": {
    "header": {
      "supplier": { "code": "SUP001" },
      "headerReference": "PDA-001",
      "dates": { "date": "2024-01-15" },
      "period": { "year": "Current" }
    },
    "detailLines": [
      {
        "action": "Insert",
        "detail": "Adjustment",
        "analysis": { "code": "ADJ" },
        "values": { "net": 50.00 }
      }
    ]
  }
}`,
          },
          update: {
            name: 'Update Debit Adjustment',
            description: 'Update a purchase debit adjustment',
            query: `mutation UpdatePurchaseDebitAdjustment($debitAdjustment: PurchaseTransactionInput!, $validateOnly: Boolean) {
  creditors {
    transactions {
      debitAdjustment {
        update(debitAdjustment: $debitAdjustment, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "debitAdjustment": {
    "header": { "primaryKey": 123 }
  }
}`,
          },
        },
      },
      purchaseCreditAdjustment: {
        name: 'Purchase Credit Adjustments',
        description: 'Purchase credit adjustment transactions',
        actions: {
          create: {
            name: 'Create Credit Adjustment',
            description: 'Create a purchase credit adjustment',
            query: `mutation CreatePurchaseCreditAdjustment($creditAdjustment: PurchaseTransactionInput!, $validateOnly: Boolean) {
  creditors {
    transactions {
      creditAdjustment {
        create(creditAdjustment: $creditAdjustment, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "creditAdjustment": {
    "header": {
      "supplier": { "code": "SUP001" },
      "headerReference": "PCA-001",
      "dates": { "date": "2024-01-15" },
      "period": { "year": "Current" }
    },
    "detailLines": [
      {
        "action": "Insert",
        "detail": "Adjustment",
        "analysis": { "code": "ADJ" },
        "values": { "net": 50.00 }
      }
    ]
  }
}`,
          },
          update: {
            name: 'Update Credit Adjustment',
            description: 'Update a purchase credit adjustment',
            query: `mutation UpdatePurchaseCreditAdjustment($creditAdjustment: PurchaseTransactionInput!, $validateOnly: Boolean) {
  creditors {
    transactions {
      creditAdjustment {
        update(creditAdjustment: $creditAdjustment, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "creditAdjustment": {
    "header": { "primaryKey": 123 }
  }
}`,
          },
        },
      },
      purchaseCashAllocation: {
        name: 'Purchase Cash Allocation',
        description: 'Allocate payments to invoices',
        actions: {
          process: {
            name: 'Process Cash Allocation',
            description: 'Allocate payments to supplier invoices',
            query: `mutation ProcessPurchaseCashAllocation($data: CashAllocationProcessInput!) {
  creditors {
    transactions {
      cashAllocation {
        processAllocations(data: $data) {
          ... on CashAllocationResult {
            allocationRef
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "data": {
    "date": "2024-01-20",
    "period": { "year": "Current", "number": 1 },
    "transactions": [
      { "primaryKey": 123, "amount": 600.00 }
    ]
  }
}`,
          },
          reverse: {
            name: 'Reverse Cash Allocation',
            description: 'Reverse a purchase cash allocation',
            query: `mutation ReversePurchaseCashAllocation($data: CashAllocationProcessInput!) {
  creditors {
    transactions {
      reverseAllocation {
        processAllocationReversals(data: $data) {
          ... on CashAllocationResult {
            allocationRef
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "data": {
    "date": "2024-01-20",
    "period": { "year": "Current", "number": 1 },
    "transactions": [
      { "primaryKey": 123, "amount": 600.00 }
    ]
  }
}`,
          },
        },
      },
      purchaseBulkTransactions: {
        name: 'Purchase Bulk Transactions',
        description: 'Bulk create/update purchase transactions',
        actions: {
          bulkCreate: {
            name: 'Bulk Create Purchase Transactions',
            description: 'Create multiple purchase transactions in one call',
            query: `mutation BulkCreatePurchaseTransactions($transactions: [PurchaseTransactionBulkInput!]!, $validateOnly: Boolean) {
  creditors {
    transactions {
      bulkTransactions {
        bulkCreate(transactions: $transactions, validateOnly: $validateOnly) {
          ... on IntEntityIds {
            ids
          }
          ... on FailedValidation {
            errors { message details { primaryKey field value } }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "transactions": [
    {
      "transactionType": "Invoice",
      "index": "1",
      "header": {
        "supplier": { "code": "SUP001" },
        "headerReference": "BULK-001",
        "dates": { "date": "2024-01-15" },
        "period": { "year": "Current" }
      },
      "detailLines": [
        {
          "action": "Insert",
          "detail": "Item 1",
          "analysis": { "code": "PURCH" },
          "values": { "net": 100.00 }
        }
      ]
    }
  ]
}`,
          },
          bulkUpdate: {
            name: 'Bulk Update Purchase Transactions',
            description: 'Update multiple purchase transactions',
            query: `mutation BulkUpdatePurchaseTransactions($transactions: [PurchaseTransactionBulkInput!]!, $validateOnly: Boolean) {
  creditors {
    transactions {
      bulkTransactions {
        bulkUpdate(transactions: $transactions, validateOnly: $validateOnly) {
          ... on IntEntityIds {
            ids
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "transactions": [
    {
      "transactionType": "Invoice",
      "header": { "primaryKey": 123 }
    }
  ]
}`,
          },
        },
      },
      purchaseBatchedTransactions: {
        name: 'Purchase Batched Transactions',
        description: 'Manage batched purchase transactions',
        actions: {
          post: {
            name: 'Post Batched Transactions',
            description: 'Post purchase transactions from batch to live',
            query: `mutation PostPurchaseBatchedTransactions($primaryKeys: [Int!]!, $postToRestrictedPeriod: Boolean) {
  creditors {
    transactions {
      batchedTransactions {
        post(primaryKeys: $primaryKeys, postToRestrictedPeriod: $postToRestrictedPeriod) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "primaryKeys": [123, 456],
  "postToRestrictedPeriod": false
}`,
          },
          delete: {
            name: 'Delete Batched Transactions',
            description: 'Delete batched purchase transactions',
            query: `mutation DeletePurchaseBatchedTransactions($primaryKeys: [Int!]!) {
  creditors {
    transactions {
      batchedTransactions {
        delete(primaryKeys: $primaryKeys) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "primaryKeys": [123]
}`,
          },
        },
      },
      invoiceRegister: {
        name: 'Invoice Register',
        description: 'Invoice register for approval workflow',
        actions: {
          createLedgerInvoice: {
            name: 'Create Ledger Invoice',
            description: 'Create an invoice register ledger invoice',
            query: `mutation CreateInvoiceRegisterLedgerInvoice($invoice: InvoiceRegisterTransactionInput!, $validateOnly: Boolean, $discardInvalidDetailLines: Boolean, $selectApproverLater: Boolean) {
  creditors {
    transactions {
      invoiceRegister {
        ledgerInvoice {
          create(invoice: $invoice, validateOnly: $validateOnly, discardInvalidDetailLines: $discardInvalidDetailLines, selectApproverLater: $selectApproverLater) {
            ... on IntEntityId {
              id
            }
            ... on FailedValidation {
              errors { message details { primaryKey field value } }
              warnings { message }
            }
            ... on UnexpectedError {
              errorMessage
            }
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "discardInvalidDetailLines": false,
  "selectApproverLater": false,
  "invoice": {
    "header": {
      "supplier": { "code": "SUP001" },
      "headerReference": "IR-001",
      "description": "Invoice for approval",
      "dates": { "date": "2024-01-15" },
      "period": { "year": "Current" }
    },
    "invoiceRegister": {
      "approveNow": false,
      "orderNumber": "PO-001"
    },
    "detailLines": [
      {
        "action": "Insert",
        "detail": "Goods",
        "analysis": { "code": "PURCH" },
        "tax": { "code": "T1" },
        "values": { "net": 500.00, "tax": 100.00 }
      }
    ]
  }
}`,
          },
          createOrderInvoice: {
            name: 'Create Order Invoice',
            description: 'Create an invoice register order invoice',
            query: `mutation CreateInvoiceRegisterOrderInvoice($invoice: InvoiceRegisterTransactionInput!, $validateOnly: Boolean, $discardInvalidDetailLines: Boolean, $selectApproverLater: Boolean, $convertOrderNumber: Boolean) {
  creditors {
    transactions {
      invoiceRegister {
        orderInvoice {
          create(invoice: $invoice, validateOnly: $validateOnly, discardInvalidDetailLines: $discardInvalidDetailLines, selectApproverLater: $selectApproverLater, convertOrderNumber: $convertOrderNumber) {
            ... on IntEntityId {
              id
            }
            ... on FailedValidation {
              errors { message }
              warnings { message }
            }
            ... on UnexpectedError {
              errorMessage
            }
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "invoice": {
    "header": {
      "supplier": { "code": "SUP001" },
      "headerReference": "IR-ORD-001"
    },
    "invoiceRegister": {
      "orderNumber": "PO-001"
    }
  }
}`,
          },
          bulkCreateLedgerInvoices: {
            name: 'Bulk Create Ledger Invoices',
            description: 'Create multiple invoice register ledger invoices',
            query: `mutation BulkCreateInvoiceRegisterLedgerInvoices($invoices: [InvoiceRegisterTransactionBulkInput!]!, $validateOnly: Boolean, $discardInvalidDetailLines: Boolean, $selectApproverLater: Boolean) {
  creditors {
    transactions {
      invoiceRegister {
        ledgerInvoice {
          bulkCreate(invoices: $invoices, validateOnly: $validateOnly, discardInvalidDetailLines: $discardInvalidDetailLines, selectApproverLater: $selectApproverLater) {
            ... on IntEntityIds {
              ids
            }
            ... on FailedValidation {
              errors { message }
              warnings { message }
            }
            ... on UnexpectedError {
              errorMessage
            }
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "invoices": [
    {
      "index": "1",
      "header": {
        "supplier": { "code": "SUP001" },
        "headerReference": "BULK-IR-001"
      },
      "detailLines": [
        {
          "action": "Insert",
          "analysis": { "code": "PURCH" },
          "values": { "net": 100.00 }
        }
      ]
    }
  ]
}`,
          },
          delete: {
            name: 'Delete Invoice Register',
            description: 'Delete invoice register transactions',
            query: `mutation DeleteInvoiceRegister($primaryKeys: [Int!]!) {
  creditors {
    transactions {
      invoiceRegister {
        actions {
          delete(primaryKeys: $primaryKeys) {
            ... on Succeeded {
              succeeded
            }
            ... on FailedValidation {
              errors { message }
              warnings { message }
            }
            ... on UnexpectedError {
              errorMessage
            }
          }
        }
      }
    }
  }
}`,
            variables: `{
  "primaryKeys": [123]
}`,
          },
          listTransactions: {
            name: 'List Invoice Register Transactions',
            description: 'Get a paginated list of invoice register transaction headers',
            query: `query ListInvoiceRegisterTransactions($first: Int, $after: String, $filter: InvoiceRegisterMainHeaderFilterInput, $sort: [InvoiceRegisterMainHeaderSortSortByInput!]) {
  creditors {
    transactions {
      invoiceRegisterTransactions {
        header(first: $first, after: $after, filter: $filter, sort: $sort) {
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
          items {
            auditNo
            headerRef
            transactionType
            description
            accountCode
            accountName
            orderNumber
            status
            onDispute
            date
            approverName
            approverId
            matchStatus
            gross
            net
            tax
            currencyGross
            currencyNet
            currencyTax
            yearLabel
            periodNumber
            rejected
            approver2Name
            approver3Name
            dueDate
            currencyCode
            origin
            dateReceived
            batchRef
            subLedger
            hasDetailLines
          }
        }
      }
    }
  }
}`,
            variables: `{
  "first": 50,
  "after": null,
  "filter": null,
  "sort": [{ "field": "AuditNo", "order": "DESC" }]
}`,
          },
          listTransactionDetails: {
            name: 'List Invoice Register Transaction Details',
            description: 'Get a paginated list of invoice register transaction detail lines',
            query: `query ListInvoiceRegisterTransactionDetails($first: Int, $after: String, $filter: InvoiceRegisterMainDetailFilterInput, $sort: [InvoiceRegisterMainDetailSortSortByInput!]) {
  creditors {
    transactions {
      invoiceRegisterTransactions {
        details(first: $first, after: $after, filter: $filter, sort: $sort) {
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
          items {
            auditNo
            headerKey
            analysisCode
            analysisName
            net
            tax
            gross
            currencyNet
            currencyTax
            currencyGross
            currencySymbol
            projectCode
            projectName
            costCentreCode
            costCentreName
          }
        }
      }
    }
  }
}`,
            variables: `{
  "first": 50,
  "after": null,
  "filter": {
    "headerKey": { "eq": "123" }
  },
  "sort": [{ "field": "AuditNo", "order": "ASC" }]
}`,
          },
          getTransactionTotals: {
            name: 'Get Invoice Register Transaction Totals',
            description: 'Get aggregated totals for invoice register transactions',
            query: `query GetInvoiceRegisterTransactionTotals($filter: InvoiceRegisterMainHeaderFilterInput) {
  creditors {
    transactions {
      invoiceRegisterTransactions {
        headerTotals(filter: $filter) {
          count
          netSum
          taxSum
          grossSum
          currencyNetSum
          currencyTaxSum
          currencyGrossSum
          currencyCount
          currencyCode
        }
        quickFilterCounts {
          awaitingUpdate
          awaitingMatch
          awaitingApproval
        }
      }
    }
  }
}`,
            variables: `{
  "filter": null
}`,
          },
          listApprovalStatus: {
            name: 'List Invoice Register Approval Status',
            description: 'Get a paginated list of invoice register approval status headers',
            query: `query ListInvoiceRegisterApprovalStatus($first: Int, $after: String, $filter: InvoiceRegisterApprovalStatusHeaderFilterInput, $sort: [InvoiceRegisterApprovalStatusHeaderSortSortByInput!]) {
  creditors {
    transactions {
      invoiceRegisterApprovalStatus {
        header(first: $first, after: $after, filter: $filter, sort: $sort) {
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
          items {
            auditNo
            name
            date
            receivedDate
            supplierCode
            supplierName
            headerRef
            net
            tax
            gross
            periodNumber
            yearLabel
            hasChildren
            approvalStatus
            batchRef
            invoiceUserId
            subLedger
            approverUser1
            approverUser2
            approverUser3
            approver1Name
            approver2Name
            approver3Name
            transactionType
            hasDetailLines
            onStop
            onDispute
          }
        }
      }
    }
  }
}`,
            variables: `{
  "first": 50,
  "after": null,
  "filter": null,
  "sort": [{ "field": "AuditNo", "order": "DESC" }]
}`,
          },
          listApprovalStatusDetails: {
            name: 'List Invoice Register Approval Status Details',
            description: 'Get a paginated list of invoice register approval status detail lines',
            query: `query ListInvoiceRegisterApprovalStatusDetails($first: Int, $after: String, $filter: InvoiceRegisterApprovalStatusDetailFilterInput, $sort: [InvoiceRegisterApprovalStatusDetailSortSortByInput!]) {
  creditors {
    transactions {
      invoiceRegisterApprovalStatus {
        details(first: $first, after: $after, filter: $filter, sort: $sort) {
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
          items {
            auditNo
            headerPrimary
            analysisCode
            analysisName
            date
            net
            tax
            gross
            projectCode
            projectName
            costCentreCode
            costCentreName
            receivedDate
          }
        }
      }
    }
  }
}`,
            variables: `{
  "first": 50,
  "after": null,
  "filter": {
    "headerPrimary": { "eq": 123 }
  },
  "sort": [{ "field": "AuditNo", "order": "ASC" }]
}`,
          },
          getApprovalStatusTotals: {
            name: 'Get Invoice Register Approval Status Totals',
            description: 'Get aggregated totals for invoice register approval status',
            query: `query GetInvoiceRegisterApprovalStatusTotals($filter: InvoiceRegisterApprovalStatusHeaderFilterInput) {
  creditors {
    transactions {
      invoiceRegisterApprovalStatus {
        headerTotals(filter: $filter) {
          netSum
          grossSum
          taxSum
        }
      }
    }
  }
}`,
            variables: `{
  "filter": null
}`,
          },
          getEntry: {
            name: 'Get Invoice Register Entry',
            description: 'Get a single invoice register entry by ID with full details',
            query: `query GetInvoiceRegisterEntry($id: Int!) {
  creditors {
    transactions {
      invoiceRegisterEntry {
        header(id: $id) {
          transactionType
          hasNextPage
          header {
            primaryKey
            headerReference
            description
            supplier {
              code
              name
              onStop
            }
            period {
              year
              number
            }
            dates {
              date
              receivedDate
              dueDate
            }
            values {
              net
              tax
              gross
              currencyNet
              currencyTax
              currencyGross
            }
            subLedger {
              code
              description
            }
            currency {
              code
              symbol
              rate
            }
          }
          invoiceRegister {
            flag
            status
            type
            approver1UserId
            approver2UserId
            approver3UserId
            approveStatus
            orderNumber
            project
            costCentre
          }
          options {
            batched
            batchReference
            transactionValuesEntry
          }
          detailLines {
            primaryKey
            detail
            analysis {
              code
              name
            }
            tax {
              code
            }
            values {
              net
              tax
              gross
              currencyNet
              currencyTax
              currencyGross
            }
            costing {
              project {
                code
                name
              }
              costCentre {
                code
                name
              }
            }
          }
        }
      }
    }
  }
}`,
            variables: `{
  "id": 123
}`,
          },
          listMatchingOrderLines: {
            name: 'List Matching Purchase Order Lines',
            description: 'Get purchase order detail lines that can be matched to invoice register',
            query: `query ListInvoiceRegisterMatchingOrderLines($first: Int, $after: String, $filter: InvoiceRegisterMatchingOrderLineFilterInput, $sort: [InvoiceRegisterMatchingOrderLineSortSortByInput!]) {
  creditors {
    transactions {
      invoiceRegisterTransactions {
        purchaseOrderDetailLines(first: $first, after: $after, filter: $filter, sort: $sort) {
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
          items {
            orderNumber
            accountCode
            accountName
            currencyCode
            matchedFlag
            orderType
            itemCode
            detail
            quantityOrdered
            quantityReceived
            quantityInvoiced
            quantityMatched
            matchPartialQuantity
            homePrice
            price
            net
            tax
            gross
            taxCode
            taxRate
            project
            costCentre
            analysisCode
            orderDate
            requiredDate
            orderYear
            periodNumber
            yearLabel
            orderReference
            headerKey
          }
        }
      }
    }
  }
}`,
            variables: `{
  "first": 50,
  "after": null,
  "filter": {
    "accountCode": { "eq": "SUP001" }
  },
  "sort": [{ "field": "OrderNumber", "order": "DESC" }]
}`,
          },
          updateLedgerInvoice: {
            name: 'Update Ledger Invoice',
            description: 'Update an existing invoice register ledger invoice (e.g., reassign approvers)',
            query: `mutation UpdateInvoiceRegisterLedgerInvoice($invoice: InvoiceRegisterTransactionInput!, $validateOnly: Boolean) {
  creditors {
    transactions {
      invoiceRegister {
        ledgerInvoice {
          update(invoice: $invoice, validateOnly: $validateOnly) {
            ... on IntEntityId {
              id
            }
            ... on FailedValidation {
              errors { message details { primaryKey field value } }
              warnings { message }
            }
            ... on UnexpectedError {
              errorMessage
            }
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "invoice": {
    "header": {
      "primaryKey": 123
    },
    "invoiceRegister": {
      "approver1UserId": "new.approver@company.com",
      "approver2UserId": null,
      "approver3UserId": null
    }
  }
}`,
          },
          updateOrderInvoice: {
            name: 'Update Order Invoice',
            description: 'Update an existing invoice register order invoice (e.g., reassign approvers)',
            query: `mutation UpdateInvoiceRegisterOrderInvoice($invoice: InvoiceRegisterTransactionInput!, $validateOnly: Boolean) {
  creditors {
    transactions {
      invoiceRegister {
        orderInvoice {
          update(invoice: $invoice, validateOnly: $validateOnly) {
            ... on IntEntityId {
              id
            }
            ... on FailedValidation {
              errors { message details { primaryKey field value } }
              warnings { message }
            }
            ... on UnexpectedError {
              errorMessage
            }
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "invoice": {
    "header": {
      "primaryKey": 123
    },
    "invoiceRegister": {
      "approver1UserId": "new.approver@company.com"
    }
  }
}`,
          },
          bulkUpdateLedgerInvoices: {
            name: 'Bulk Update Ledger Invoices',
            description: 'Update multiple invoice register ledger invoices (e.g., bulk reassign approvers)',
            query: `mutation BulkUpdateInvoiceRegisterLedgerInvoices($invoices: [InvoiceRegisterTransactionBulkInput!]!, $validateOnly: Boolean) {
  creditors {
    transactions {
      invoiceRegister {
        ledgerInvoice {
          bulkUpdate(invoices: $invoices, validateOnly: $validateOnly) {
            ... on IntEntityIds {
              ids
            }
            ... on FailedValidation {
              errors { message details { primaryKey field value } }
              warnings { message }
            }
            ... on UnexpectedError {
              errorMessage
            }
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "invoices": [
    {
      "index": "1",
      "header": {
        "primaryKey": 123
      },
      "invoiceRegister": {
        "approver1UserId": "new.approver@company.com"
      }
    },
    {
      "index": "2",
      "header": {
        "primaryKey": 456
      },
      "invoiceRegister": {
        "approver1UserId": "new.approver@company.com"
      }
    }
  ]
}`,
          },
          reassignApprovers: {
            name: 'Reassign Approvers (Bulk)',
            description: 'Reassign approvers for multiple invoice register transactions - useful when an approver leaves',
            query: `mutation ReassignInvoiceRegisterApprovers($invoices: [InvoiceRegisterTransactionBulkInput!]!, $validateOnly: Boolean) {
  creditors {
    transactions {
      invoiceRegister {
        ledgerInvoice {
          bulkUpdate(invoices: $invoices, validateOnly: $validateOnly) {
            ... on IntEntityIds {
              ids
            }
            ... on FailedValidation {
              errors { message details { primaryKey field value } }
              warnings { message }
            }
            ... on UnexpectedError {
              errorMessage
            }
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "invoices": [
    {
      "index": "1",
      "header": { "primaryKey": 123 },
      "invoiceRegister": {
        "approver1UserId": "replacement.approver@company.com"
      }
    },
    {
      "index": "2",
      "header": { "primaryKey": 456 },
      "invoiceRegister": {
        "approver1UserId": "replacement.approver@company.com"
      }
    }
  ]
}`,
          },
        },
      },
      suggestedPayments: {
        name: 'Suggested Payments',
        description: 'Suggested payment processing',
        actions: {
          process: {
            name: 'Process Suggested Payments',
            description: 'Process suggested supplier payments',
            query: `mutation ProcessSuggestedPayments($data: SuggestedPaymentsProcessInput!, $validateOnly: Boolean) {
  creditors {
    transactions {
      suggestedPayment {
        processSuggestedPayments(data: $data, validateOnly: $validateOnly) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "data": {
    "currencyCode": "GBP",
    "transactions": [
      {
        "auditNo": 123,
        "valueToAllocate": 500.00,
        "settlementDiscount": 0,
        "approvalStage": "NoPaymentApproval"
      }
    ]
  }
}`,
          },
        },
      },
      remittance: {
        name: 'Remittance',
        description: 'Remittance and BACS processing',
        actions: {
          processBacs: {
            name: 'Process BACS Remittance',
            description: 'Process BACS remittance payments',
            query: `mutation ProcessRemittanceBacs($data: RemittanceBacsProcessInput!, $validateOnly: Boolean) {
  creditors {
    transactions {
      remittanceTransactions {
        processBacs(data: $data, validateOnly: $validateOnly) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "data": {
    "bacsHeaderReference": "BACS-001",
    "allocationDate": "2024-01-20",
    "allocationYear": "Current",
    "allocationPeriod": 1,
    "paymentDate": "2024-01-22",
    "paymentYear": "Current",
    "paymentPeriod": 1,
    "bankAnalysis": "BANK",
    "currency": { "symbol": "£", "code": "GBP" },
    "transactions": [
      {
        "auditNo": 123,
        "valueToAllocate": 500.00
      }
    ]
  }
}`,
          },
          processCheques: {
            name: 'Process Cheque Remittance',
            description: 'Process cheque remittance payments',
            query: `mutation ProcessRemittanceCheques($data: RemittanceChequesProcessInput!, $validateOnly: Boolean) {
  creditors {
    transactions {
      remittanceTransactions {
        processCheques(data: $data, validateOnly: $validateOnly) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "data": {
    "allocationDate": "2024-01-20",
    "allocationYear": "Current",
    "allocationPeriod": 1,
    "paymentDate": "2024-01-22",
    "paymentYear": "Current",
    "paymentPeriod": 1,
    "bankAnalysis": "BANK",
    "chequeNumber": "000123",
    "incrementChequeNo": true,
    "currency": { "symbol": "£" },
    "transactions": [
      {
        "auditNo": 123,
        "valueToAllocate": 500.00
      }
    ]
  }
}`,
          },
          printBacs: {
            name: 'Print BACS Remittance',
            description: 'Prepare BACS remittance for printing',
            query: `mutation PrintRemittanceBacs($data: RemittanceBacsProcessInput!) {
  creditors {
    transactions {
      remittanceTransactions {
        printBacs(data: $data) {
          ... on RemittanceProcessDefaults {
            batchId
            allocationDate
            paymentDate
            differenceAmount
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "data": {
    "bacsHeaderReference": "BACS-001",
    "allocationDate": "2024-01-20",
    "paymentDate": "2024-01-22",
    "bankAnalysis": "BANK",
    "transactions": [
      { "auditNo": 123, "valueToAllocate": 500.00 }
    ]
  }
}`,
          },
        },
      },
      bacsExport: {
        name: 'BACS Export',
        description: 'BACS export actions',
        actions: {
          confirm: {
            name: 'Confirm BACS Export',
            description: 'Confirm BACS export payment status',
            query: `mutation ConfirmBacsExport($input: BacsExportConfirmInput!) {
  creditors {
    transactions {
      bacsExport {
        confirm(input: $input) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "input": {
    "paymentReference": "BACS-CONF-001",
    "date": "2024-01-22",
    "primaryKeys": [123, 456]
  }
}`,
          },
          delete: {
            name: 'Clear BACS Export',
            description: 'Clear BACS export payment status',
            query: `mutation ClearBacsExport($primaryKeys: [Int!]!) {
  creditors {
    transactions {
      bacsExport {
        delete(primaryKeys: $primaryKeys) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "primaryKeys": [123]
}`,
          },
        },
      },
      purchaseTransactionActions: {
        name: 'Purchase Transaction Actions',
        description: 'Common purchase transaction actions',
        actions: {
          dispute: {
            name: 'Dispute Transaction',
            description: 'Link a query reason to a purchase transaction',
            query: `mutation DisputePurchaseTransaction($transactionDisputeReason: TransactionDisputeReasonInput!) {
  creditors {
    transactions {
      transactionActions {
        dispute(transactionDisputeReason: $transactionDisputeReason) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "transactionDisputeReason": {
    "transactionPrimaryKey": 123,
    "queryReasonPrimaryKey": 1,
    "queryFlag": true,
    "note": "Supplier dispute - incorrect goods"
  }
}`,
          },
          delete: {
            name: 'Delete Transaction',
            description: 'Delete a purchase transaction',
            query: `mutation DeletePurchaseTransaction($primaryKey: Int!) {
  creditors {
    transactions {
      transactionActions {
        delete(primaryKey: $primaryKey) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "primaryKey": 123
}`,
          },
        },
      },
      rejectionReasons: {
        name: 'Rejection Reasons',
        description: 'Invoice register rejection reasons',
        actions: {
          create: {
            name: 'Create Rejection Reason',
            description: 'Create a new rejection reason',
            query: `mutation CreateRejectionReason($rejectionReason: RejectionReasonInput!) {
  creditors {
    masterRecords {
      rejectionReason {
        create(rejectionReason: $rejectionReason) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "rejectionReason": {
    "code": "REJ01",
    "text": "Incorrect pricing"
  }
}`,
          },
          update: {
            name: 'Update Rejection Reason',
            description: 'Update an existing rejection reason',
            query: `mutation UpdateRejectionReason($rejectionReason: RejectionReasonInput!) {
  creditors {
    masterRecords {
      rejectionReason {
        update(rejectionReason: $rejectionReason) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "rejectionReason": {
    "primaryKey": 123,
    "text": "Updated rejection reason"
  }
}`,
          },
          delete: {
            name: 'Delete Rejection Reasons',
            description: 'Delete rejection reasons',
            query: `mutation DeleteRejectionReasons($rejectionReasons: [Int!]!, $validateOnly: Boolean) {
  creditors {
    masterRecords {
      rejectionReason {
        delete(rejectionReasons: $rejectionReasons, validateOnly: $validateOnly) {
          ... on IntEntityIds {
            ids
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "rejectionReasons": [123]
}`,
          },
        },
      },
    },
  },

  // ============================================================
  // FINANCIALS (Accounts, Journals)
  // ============================================================
  financials: {
    name: 'Financials',
    description: 'General ledger accounts and financial transactions',
    resources: {
      accounts: {
        name: 'Nominal Accounts',
        description: 'Chart of accounts / General ledger accounts',
        actions: {
          list: {
            name: 'List Accounts (Summary)',
            description: 'Get a paginated list of nominal accounts',
            query: `query ListAccounts($first: Int, $after: String, $filter: NominalAccountSummaryFilterInput) {
  financials {
    lists {
      accounts(first: $first, after: $after, filter: $filter) {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        items {
          primaryKey
          code
          accountName
          accountType
          majorHeading
          currency
          compulsoryCosting
          costCentre
          inactive
        }
      }
    }
  }
}`,
            variables: `{
  "first": 100,
  "after": null,
  "filter": null
}`,
          },
          listFull: {
            name: 'List Accounts (Full Details)',
            description: 'Get full nominal account records',
            query: `query ListAccountsFull($first: Int, $after: String, $filter: NominalAccountFilterInput) {
  financials {
    masterRecords {
      accounts {
        records(first: $first, after: $after, filter: $filter) {
          pageInfo {
            hasNextPage
            endCursor
          }
          items {
            details {
              primaryKey
              accountCode
              accountName
              accountType
              majorHeadingCode
              majorHeadingName
              currencyCode
              costCentre
              compulsoryCosting
            }
            settings {
              inactive
              allowZeroBudgets
            }
          }
        }
      }
    }
  }
}`,
            variables: `{
  "first": 100,
  "after": null,
  "filter": null
}`,
          },
          getByCode: {
            name: 'Get Account by Code',
            description: 'Retrieve a specific account by code',
            query: `query GetAccount($code: String!) {
  financials {
    lists {
      accounts(first: 1, filter: { code: { eq: $code } }) {
        items {
          primaryKey
          code
          accountName
          accountType
          majorHeading
          currency
          inactive
        }
      }
    }
  }
}`,
            variables: `{
  "code": "1000"
}`,
          },
        },
      },
      cashBooks: {
        name: 'Cash Books',
        description: 'Bank accounts and cash management',
        actions: {
          list: {
            name: 'List Cash Books',
            description: 'Get a list of cash book accounts',
            query: `query ListCashBooks($first: Int, $after: String) {
  financials {
    masterRecords {
      cashBook {
        record(first: $first, after: $after) {
          pageInfo {
            hasNextPage
            endCursor
          }
          items {
            primaryKey
            accountCode
            accountName
            currencyCode
            inactive
            balanceHome
            balanceCurrency
            unreconciledHome
            unreconciledCurrency
          }
        }
      }
    }
  }
}`,
            variables: `{
  "first": 50,
  "after": null
}`,
          },
          updateBankDetails: {
            name: 'Update Bank Details',
            description: 'Update cash book bank details',
            query: `mutation UpdateCashBookBankDetails($bankDetails: CashBookBankDetailsInput!) {
  financials {
    masterRecords {
      cashBook {
        updateBankDetails(bankDetails: $bankDetails) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "bankDetails": {
    "primaryKey": 123,
    "accountCode": "1200",
    "bankName": "HSBC",
    "bankAccountName": "Business Account",
    "bankAccountNo": "12345678",
    "sortCode": "40-00-00"
  }
}`,
          },
        },
      },
      nominalAccounts: {
        name: 'Nominal Accounts',
        description: 'Nominal account management',
        actions: {
          create: {
            name: 'Create Nominal Account',
            description: 'Create a new nominal account',
            query: `mutation CreateNominalAccount($account: NominalAccountInput!) {
  financials {
    masterRecords {
      accounts {
        create(account: $account) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message details { primaryKey field value } }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "account": {
    "details": {
      "accountCode": "5000",
      "accountName": "Sales Revenue",
      "accountType": "Nominal",
      "majorHeadingCode": 4,
      "currencyCode": "GBP"
    },
    "settings": {
      "reportingCodeCode": 0,
      "inactive": false
    }
  }
}`,
          },
          update: {
            name: 'Update Nominal Account',
            description: 'Update an existing nominal account',
            query: `mutation UpdateNominalAccount($account: NominalAccountInput!) {
  financials {
    masterRecords {
      accounts {
        update(account: $account) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "account": {
    "details": {
      "primaryKey": 123,
      "accountName": "Updated Account Name"
    }
  }
}`,
          },
          delete: {
            name: 'Delete Nominal Account',
            description: 'Delete nominal accounts',
            query: `mutation DeleteNominalAccounts($accounts: [DeleteRecordItemInput!]!, $validateOnly: Boolean, $deleteAnalysisCodes: Boolean) {
  financials {
    masterRecords {
      accounts {
        delete(accounts: $accounts, validateOnly: $validateOnly, deleteAnalysisCodes: $deleteAnalysisCodes) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "deleteAnalysisCodes": false,
  "accounts": [{ "code": "5000" }]
}`,
          },
          changeCode: {
            name: 'Change Account Code',
            description: 'Change a nominal account code',
            query: `mutation ChangeNominalAccountCode($codes: NominalAccountCodesInput!) {
  financials {
    masterRecords {
      accounts {
        changeCode(codes: $codes) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "codes": {
    "codeFrom": "5000",
    "codeTo": "5001"
  }
}`,
          },
          mergeCode: {
            name: 'Merge Account Codes',
            description: 'Merge one nominal account code into another',
            query: `mutation MergeNominalAccountCode($codes: NominalAccountCodesInput!) {
  financials {
    masterRecords {
      accounts {
        mergeCode(codes: $codes) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "codes": {
    "codeFrom": "5000",
    "codeTo": "5001"
  }
}`,
          },
        },
      },
      nominalJournals: {
        name: 'Nominal Journals',
        description: 'Nominal journal transactions',
        actions: {
          create: {
            name: 'Create Nominal Journal',
            description: 'Create a new nominal journal',
            query: `mutation CreateNominalJournal($journal: NominalTransactionInput!, $validateOnly: Boolean) {
  financials {
    transactions {
      journal {
        create(journal: $journal, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message details { primaryKey field value } }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "journal": {
    "module": "NominalLedger",
    "header": {
      "headerReference": "JNL-001",
      "description": "Monthly accrual",
      "transactionDate": "2024-01-31",
      "period": { "year": "Current", "number": 1 }
    },
    "options": {
      "batched": false,
      "transactionValuesEntry": "HomeNet"
    },
    "detailLines": [
      {
        "action": "Insert",
        "detail": "Accrued expenses",
        "nominalUpdate": {
          "debit": { "code": "6000" },
          "credit": { "code": "2100" }
        },
        "values": { "net": 1000.00 }
      }
    ]
  }
}`,
          },
          update: {
            name: 'Update Nominal Journal',
            description: 'Update an existing nominal journal',
            query: `mutation UpdateNominalJournal($journal: NominalTransactionInput!, $validateOnly: Boolean) {
  financials {
    transactions {
      journal {
        update(journal: $journal, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "journal": {
    "header": {
      "primaryKey": 123,
      "description": "Updated description"
    }
  }
}`,
          },
        },
      },
      taxJournals: {
        name: 'Tax Journals',
        description: 'Tax journal transactions',
        actions: {
          create: {
            name: 'Create Tax Journal',
            description: 'Create a new tax journal',
            query: `mutation CreateTaxJournal($taxJournal: NominalTransactionInput!, $validateOnly: Boolean) {
  financials {
    transactions {
      taxJournal {
        create(taxJournal: $taxJournal, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message details { primaryKey field value } }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "taxJournal": {
    "header": {
      "headerReference": "TJ-001",
      "description": "VAT adjustment",
      "transactionDate": "2024-01-31",
      "period": { "year": "Current" }
    },
    "detailLines": [
      {
        "action": "Insert",
        "detail": "VAT adjustment",
        "tax": { "code": "T1" },
        "nominalUpdate": {
          "debit": { "code": "2200" },
          "credit": { "code": "2201" }
        },
        "values": { "net": 100.00, "tax": 20.00 }
      }
    ]
  }
}`,
          },
          update: {
            name: 'Update Tax Journal',
            description: 'Update an existing tax journal',
            query: `mutation UpdateTaxJournal($taxJournal: NominalTransactionInput!, $validateOnly: Boolean) {
  financials {
    transactions {
      taxJournal {
        update(taxJournal: $taxJournal, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "taxJournal": {
    "header": { "primaryKey": 123 }
  }
}`,
          },
        },
      },
      currencyTransfer: {
        name: 'Currency Transfer',
        description: 'Currency transfer transactions',
        actions: {
          create: {
            name: 'Create Currency Transfer',
            description: 'Create a currency transfer between accounts',
            query: `mutation CreateCurrencyTransfer($currencyTransfer: NominalTransactionInput!, $revaluationJournal: Boolean, $validateOnly: Boolean) {
  financials {
    transactions {
      currencyTransfer {
        create(currencyTransfer: $currencyTransfer, revaluationJournal: $revaluationJournal, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "revaluationJournal": false,
  "currencyTransfer": {
    "module": "CashBook",
    "header": {
      "headerReference": "CT-001",
      "description": "GBP to EUR transfer",
      "transactionDate": "2024-01-15",
      "period": { "year": "Current" }
    },
    "detailLines": [
      {
        "action": "Insert",
        "nominalUpdate": {
          "debit": { "code": "1201" },
          "credit": { "code": "1200" }
        },
        "values": { "net": 1000.00 },
        "currency": { "code": "EUR", "rate": 1.17 }
      }
    ]
  }
}`,
          },
          update: {
            name: 'Update Currency Transfer',
            description: 'Update a currency transfer',
            query: `mutation UpdateCurrencyTransfer($currencyTransfer: NominalTransactionInput!, $validateOnly: Boolean) {
  financials {
    transactions {
      currencyTransfer {
        update(currencyTransfer: $currencyTransfer, validateOnly: $validateOnly) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "currencyTransfer": {
    "header": { "primaryKey": 123 }
  }
}`,
          },
        },
      },
      nominalBulkTransactions: {
        name: 'Nominal Bulk Transactions',
        description: 'Bulk nominal transaction operations',
        actions: {
          bulkCreate: {
            name: 'Bulk Create Nominal Transactions',
            description: 'Create multiple nominal transactions',
            query: `mutation BulkCreateNominalTransactions($transactions: [NominalTransactionBulkInput!]!, $validateOnly: Boolean) {
  financials {
    transactions {
      bulkTransactions {
        bulkCreate(transactions: $transactions, validateOnly: $validateOnly) {
          ... on IntEntityIds {
            ids
          }
          ... on FailedValidation {
            errors { message details { primaryKey field value } }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "transactions": [
    {
      "transactionType": "Journal",
      "index": "1",
      "module": "NominalLedger",
      "header": {
        "headerReference": "BULK-JNL-001",
        "transactionDate": "2024-01-31",
        "period": { "year": "Current" }
      },
      "detailLines": [
        {
          "action": "Insert",
          "nominalUpdate": {
            "debit": { "code": "6000" },
            "credit": { "code": "2100" }
          },
          "values": { "net": 500.00 }
        }
      ]
    }
  ]
}`,
          },
          bulkUpdate: {
            name: 'Bulk Update Nominal Transactions',
            description: 'Update multiple nominal transactions',
            query: `mutation BulkUpdateNominalTransactions($transactions: [NominalTransactionBulkInput!]!, $validateOnly: Boolean) {
  financials {
    transactions {
      bulkTransactions {
        bulkUpdate(transactions: $transactions, validateOnly: $validateOnly) {
          ... on IntEntityIds {
            ids
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "transactions": [
    {
      "transactionType": "Journal",
      "header": { "primaryKey": 123 }
    }
  ]
}`,
          },
        },
      },
      nominalBatchedTransactions: {
        name: 'Nominal Batched Transactions',
        description: 'Manage batched nominal transactions',
        actions: {
          postJournals: {
            name: 'Post Batched Journals',
            description: 'Post nominal journals from batch to live',
            query: `mutation PostNominalBatchedJournals($primaryKeys: [Int!]!, $postToRestrictedPeriod: Boolean) {
  financials {
    transactions {
      batchedTransactions {
        postJournals(primaryKeys: $primaryKeys, postToRestrictedPeriod: $postToRestrictedPeriod) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "primaryKeys": [123, 456],
  "postToRestrictedPeriod": false
}`,
          },
          delete: {
            name: 'Delete Batched Transactions',
            description: 'Delete batched nominal transactions',
            query: `mutation DeleteNominalBatchedTransactions($primaryKeys: [Int!]!) {
  financials {
    transactions {
      batchedTransactions {
        delete(primaryKeys: $primaryKeys) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "primaryKeys": [123]
}`,
          },
        },
      },
      bankReconciliation: {
        name: 'Bank Reconciliation',
        description: 'Bank statement reconciliation',
        actions: {
          autoMatchStart: {
            name: 'Start Auto Match',
            description: 'Start AI auto-matching of bank statement lines',
            query: `mutation StartBankReconciliationAutoMatch($input: CashBookBankReconciliationStartAiMatchInput!) {
  financials {
    transactions {
      bankReconciliation {
        autoMatchStart(input: $input) {
          ... on GuidEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "input": {
    "bankAccountNominalCode": "1200"
  }
}`,
          },
          autoMatchWrite: {
            name: 'Write Auto Match Results',
            description: 'Write the results of auto-matching',
            query: `mutation WriteBankReconciliationAutoMatch($input: CashBookBankReconciliationAiMatchWriteInput!) {
  financials {
    transactions {
      bankReconciliation {
        autoMatchWrite(input: $input) {
          ... on CashBookBankReconciliationAiMatchWriteResult {
            completed
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "input": {
    "matchId": "guid-here",
    "bankAccountNominalCode": "1200"
  }
}`,
          },
          manualMatch: {
            name: 'Manual Match',
            description: 'Manually match bank statement lines to transactions',
            query: `mutation ManualMatchBankReconciliation($input: CashBookBankReconciliationManualMatchInput!) {
  financials {
    transactions {
      bankReconciliation {
        manualMatch(input: $input) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "input": {
    "bankAccountNominalCode": "1200",
    "reconcile": true,
    "statementLines": [1, 2],
    "transactionLines": [101, 102]
  }
}`,
          },
          reconcile: {
            name: 'Reconcile Matched Transactions',
            description: 'Reconcile matched transaction lines',
            query: `mutation ReconcileBankTransactions($input: CashBookBankReconciliationReconcileInput!) {
  financials {
    transactions {
      bankReconciliation {
        reconcile(input: $input) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "input": {
    "bankAccountNominalCode": "1200",
    "transactionPrimaryKeys": [123, 456]
  }
}`,
          },
          unmatch: {
            name: 'Unmatch Transactions',
            description: 'Unmatch bank statement lines from transactions',
            query: `mutation UnmatchBankReconciliation($input: CashBookBankReconciliationUnmatchInput!) {
  financials {
    transactions {
      bankReconciliation {
        unmatch(input: $input) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "input": {
    "matches": [
      { "matchId": 123 }
    ]
  }
}`,
          },
          unreconcile: {
            name: 'Unreconcile Transactions',
            description: 'Unreconcile previously reconciled transactions',
            query: `mutation UnreconcileBankTransactions($input: CashBookBankReconciliationUnreconcileInput!) {
  financials {
    transactions {
      bankReconciliation {
        unreconcile(input: $input) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "input": {
    "bankAccountNominalCode": "1200",
    "matches": [
      { "matchId": 123 }
    ]
  }
}`,
          },
        },
      },
      chartOfAccounts: {
        name: 'Chart of Accounts',
        description: 'Chart of accounts management',
        actions: {
          save: {
            name: 'Save Chart of Accounts',
            description: 'Save changes to chart of accounts',
            query: `mutation SaveChartOfAccounts($chartOfAccounts: [ChartOfAccountsSaveInput!]!) {
  financials {
    masterRecords {
      chartOfAccounts {
        save(chartOfAccounts: $chartOfAccounts) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "chartOfAccounts": [
    {
      "currency": "GBP",
      "leafNodes": [
        {
          "level1": "Assets",
          "level2": "Current Assets",
          "level3": "Bank Accounts"
        }
      ]
    }
  ]
}`,
          },
          build: {
            name: 'Build Chart of Accounts',
            description: 'Build the chart of accounts structure',
            query: `mutation BuildChartOfAccounts($chartOfAccounts: [ChartOfAccountsBuildInput!]!) {
  financials {
    masterRecords {
      chartOfAccounts {
        build(chartOfAccounts: $chartOfAccounts) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "chartOfAccounts": [
    {
      "currency": "GBP",
      "createAnalysis": true
    }
  ]
}`,
          },
          duplicate: {
            name: 'Duplicate Chart of Accounts',
            description: 'Duplicate chart of accounts to other currencies',
            query: `mutation DuplicateChartOfAccounts($chartOfAccounts: ChartOfAccountsDuplicateInput!) {
  financials {
    masterRecords {
      chartOfAccounts {
        duplicate(chartOfAccounts: $chartOfAccounts) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "chartOfAccounts": {
    "fromCurrencySymbol": "£",
    "toCurrencyPrimaries": [2, 3]
  }
}`,
          },
        },
      },
      nominalCategories: {
        name: 'Nominal Categories',
        description: 'Nominal category management',
        actions: {
          create: {
            name: 'Create Nominal Categories',
            description: 'Create new nominal categories',
            query: `mutation CreateNominalCategories($categories: [NominalCategoryInput!]!, $validateOnly: Boolean) {
  financials {
    masterRecords {
      nominalCategories {
        create(categories: $categories, validateOnly: $validateOnly) {
          ... on IntEntityIds {
            ids
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "categories": [
    {
      "code": "CAT01",
      "name": "Operating Expenses",
      "number": 1,
      "majorHeading": 5,
      "accountType": "Nominal",
      "inactive": false
    }
  ]
}`,
          },
          update: {
            name: 'Update Nominal Categories',
            description: 'Update existing nominal categories',
            query: `mutation UpdateNominalCategories($categories: [NominalCategoryInput!]!, $validateOnly: Boolean) {
  financials {
    masterRecords {
      nominalCategories {
        update(categories: $categories, validateOnly: $validateOnly) {
          ... on IntEntityIds {
            ids
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "categories": [
    {
      "primaryKey": 123,
      "name": "Updated Category Name"
    }
  ]
}`,
          },
          delete: {
            name: 'Delete Nominal Categories',
            description: 'Delete nominal categories',
            query: `mutation DeleteNominalCategories($categories: [NominalCategoryDeleteInput!]!, $validateOnly: Boolean) {
  financials {
    masterRecords {
      nominalCategories {
        delete(categories: $categories, validateOnly: $validateOnly) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "validateOnly": false,
  "categories": [
    { "code": "CAT01", "number": 1 }
  ]
}`,
          },
        },
      },
    },
  },

  // ============================================================
  // STOCK (Inventory)
  // ============================================================
  stock: {
    name: 'Stock',
    description: 'Inventory and stock management',
    resources: {
      stockItems: {
        name: 'Stock Items',
        description: 'Stock/inventory records',
        actions: {
          list: {
            name: 'List Stock Items (Summary)',
            description: 'Get a paginated summary of stock items',
            query: `query ListStockItems($first: Int, $after: String, $filter: StockOrSubAnalysisSummaryFilterInput) {
  stock {
    lists {
      stockOrSubAnalysis(first: $first, after: $after, filter: $filter) {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        items {
          primaryKey
          stockCode
          stockName
          subAnalysisCode
          subAnalysisName
          binNumber
          physicalQuantity
          stockSortKey
        }
      }
    }
  }
}`,
            variables: `{
  "first": 50,
  "after": null,
  "filter": null
}`,
          },
          listFull: {
            name: 'List Stock Items (Full Details)',
            description: 'Get full stock item records',
            query: `query ListStockItemsFull($first: Int, $after: String, $filter: StockRecordFilterInput) {
  stock {
    masterRecords {
      stock(first: $first, after: $after, filter: $filter) {
        pageInfo {
          hasNextPage
          endCursor
        }
        items {
          stockDetails {
            primaryKey
            code
            name
            type
            physicalQuantity
            allocatedQuantity
            unallocatedQuantity
            freeStock
            sellPrice
            costPrice
            notes
            isLowStock
          }
          stockSettings {
            inactive
            minimumQuantity
            maximumQuantity
          }
        }
      }
    }
  }
}`,
            variables: `{
  "first": 50,
  "after": null,
  "filter": null
}`,
          },
          getByCode: {
            name: 'Get Stock Item by Code',
            description: 'Retrieve a specific stock item by code',
            query: `query GetStockItem($stockCode: String!) {
  stock {
    lists {
      stockOrSubAnalysis(first: 1, filter: { stockCode: { eq: $stockCode } }) {
        items {
          primaryKey
          stockCode
          stockName
          subAnalysisCode
          subAnalysisName
          binNumber
          physicalQuantity
        }
      }
    }
  }
}`,
            variables: `{
  "stockCode": "STOCK_CODE"
}`,
          },
          create: {
            name: 'Create Stock Item',
            description: 'Create a new stock/inventory record',
            query: `mutation CreateStockRecord($stockRecord: StockRecordInput!) {
  stock {
    masterRecords {
      stockRecord {
        create(stockRecord: $stockRecord) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "stockRecord": {
    "stockDetails": {
      "code": "STK001",
      "name": "New Stock Item",
      "type": "Bought",
      "sellPrice": 19.99,
      "costPrice": 10.00
    },
    "stockSettings": {
      "purchaseAnalysis": "PA001",
      "salesAnalysis": "SA001",
      "minimumQuantity": 10
    }
  }
}`,
          },
          update: {
            name: 'Update Stock Item',
            description: 'Update an existing stock record',
            query: `mutation UpdateStockRecord($stockRecord: StockRecordInput!) {
  stock {
    masterRecords {
      stockRecord {
        update(stockRecord: $stockRecord) {
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "stockRecord": {
    "stockDetails": {
      "primaryKey": 123,
      "name": "Updated Stock Item",
      "sellPrice": 24.99
    }
  }
}`,
          },
          delete: {
            name: 'Delete Stock Item',
            description: 'Delete one or more stock records',
            query: `mutation DeleteStockRecord($items: [CodeInput!]!) {
  stock {
    masterRecords {
      stockRecord {
        delete(items: $items) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "items": [{ "code": "STK001" }]
}`,
          },
          updatePrices: {
            name: 'Update Stock Selling Prices',
            description: 'Bulk update selling prices for stock items',
            query: `mutation UpdateStockSellingPrices($prices: [StockSellingPricesInput!]!) {
  stock {
    masterRecords {
      stockRecord {
        updateSellingPrices(prices: $prices) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "prices": [
    {
      "stockCode": "STK001",
      "sellPrice": 29.99
    }
  ]
}`,
          },
        },
      },
    },
  },

  // ============================================================
  // SELLING (Sales Orders)
  // ============================================================
  selling: {
    name: 'Selling',
    description: 'Sales orders and invoicing',
    resources: {
      salesOrders: {
        name: 'Sales Orders',
        description: 'Sales order management',
        actions: {
          list: {
            name: 'List Outstanding Sales Orders',
            description: 'Get outstanding sales order headers',
            query: `query ListOutstandingSalesOrders($first: Int, $after: String, $filter: OutstandingSalesOrderHeaderFilterInput) {
  selling {
    transactions {
      outstandingSalesOrders {
        header(first: $first, after: $after, filter: $filter) {
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
          items {
            orderNumber
            orderType
            orderStatus
            workStatus
            accountCode
            accountName
            customerRef
            orderDate
            orderGross
            netOutstanding
            requiredDate
            currencySymbol
            orderDescription
          }
        }
      }
    }
  }
}`,
            variables: `{
  "first": 50,
  "after": null,
  "filter": null
}`,
          },
          statuses: {
            name: 'List Sales Order Statuses',
            description: 'Get available sales order status types',
            query: `query ListSalesOrderStatuses($first: Int, $after: String) {
  selling {
    lists {
      salesOrderHeaderStatuses(first: $first, after: $after) {
        pageInfo {
          hasNextPage
          endCursor
        }
        items {
          primaryKey
          userText
          type
          isUsed
          isSystem
        }
      }
    }
  }
}`,
            variables: `{
  "first": 100,
  "after": null
}`,
          },
        },
      },
    },
  },

  // ============================================================
  // PURCHASING (Purchase Orders)
  // ============================================================
  purchasing: {
    name: 'Purchasing',
    description: 'Purchase orders and procurement',
    resources: {
      purchaseOrders: {
        name: 'Purchase Orders',
        description: 'Purchase order management',
        actions: {
          list: {
            name: 'List Purchase Orders',
            description: 'Get a paginated list of purchase orders',
            query: `query ListPurchaseOrders($first: Int, $after: String, $filter: PurchaseOrderSummaryFilterInput) {
  purchasing {
    lists {
      purchaseOrders(first: $first, after: $after, filter: $filter) {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        items {
          orderNumber
          orderType
          accountCode
          accountName
          transactionDate
          orderReference
          totalGross
          totalNet
          homeNetOutstanding
          required
          completionDate
          headerDescription
          orderStatus
        }
      }
    }
  }
}`,
            variables: `{
  "first": 50,
  "after": null,
  "filter": null
}`,
          },
        },
      },
    },
  },

  // ============================================================
  // CONFIG (Settings)
  // ============================================================
  config: {
    name: 'Config',
    description: 'System configuration and settings',
    resources: {
      company: {
        name: 'Company',
        description: 'Company information and settings',
        actions: {
          list: {
            name: 'List Company Addresses',
            description: 'Retrieve company address information',
            query: `query CompanyAddresses($first: Int, $after: String) {
  config {
    lists {
      companyAddresses(first: $first, after: $after) {
        pageInfo {
          hasNextPage
          endCursor
        }
        items {
          primaryKey
          code
          address
          town
          county
          country
          postCode
          contact
          email
          telephone
          fax
          mobile
          entity
        }
      }
    }
  }
}`,
            variables: `{
  "first": 50,
  "after": null
}`,
          },
        },
      },
      users: {
        name: 'Users',
        description: 'Financials user accounts',
        actions: {
          list: {
            name: 'List Users',
            description: 'Get a list of Financials users',
            query: `query ListUsers($first: Int, $after: String, $filter: UserSummaryFilterInput) {
  config {
    lists {
      users(first: $first, after: $after, filter: $filter) {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        items {
          userID
          name
          dateEntered
          profileName
          emailAddress
          pennyWriteOffLimit
          batchOnly
          hasExpense
        }
      }
    }
  }
}`,
            variables: `{
  "first": 100,
  "after": null,
  "filter": null
}`,
          },
          currentUser: {
            name: 'Get Current User',
            description: 'Get the currently logged in user',
            query: `query CurrentUser {
  config {
    lists {
      currentUser {
        userID
        name
        dateEntered
        profileName
        emailAddress
        pennyWriteOffLimit
        batchOnly
        hasExpense
        invoiceRegisterApproval {
          canApproveStage1
          canApproveStage2
          canApproveStage3
        }
        suggestedPayment {
          canDispute
          canApproveStage1
          canApproveStage2
        }
      }
    }
  }
}`,
            variables: `{}`,
          },
          getByEmail: {
            name: 'Get User by Email',
            description: 'Find a user by email address with approval roles',
            query: `query GetUserByEmail($email: String!) {
  config {
    lists {
      users(first: 1, filter: { emailAddress: { eq: $email } }) {
        items {
          userID
          name
          dateEntered
          profileName
          emailAddress
          pennyWriteOffLimit
          batchOnly
          hasExpense
          invoiceRegisterApproval {
            canApproveStage1
            canApproveStage2
            canApproveStage3
          }
          suggestedPayment {
            canDispute
            canApproveStage1
            canApproveStage2
          }
        }
      }
    }
  }
}`,
            variables: `{
  "email": "user@example.com"
}`,
          },
          getByUserId: {
            name: 'Get User by User ID',
            description: 'Find a user by their user ID with approval roles',
            query: `query GetUserByUserId($userId: String!) {
  config {
    lists {
      users(first: 1, filter: { userID: { eq: $userId } }) {
        items {
          userID
          name
          dateEntered
          profileName
          emailAddress
          pennyWriteOffLimit
          batchOnly
          hasExpense
          invoiceRegisterApproval {
            canApproveStage1
            canApproveStage2
            canApproveStage3
          }
          suggestedPayment {
            canDispute
            canApproveStage1
            canApproveStage2
          }
        }
      }
    }
  }
}`,
            variables: `{
  "userId": "USER001"
}`,
          },
          searchByName: {
            name: 'Search Users by Name',
            description: 'Search for users by name (partial match supported)',
            query: `query SearchUsersByName($first: Int, $after: String, $nameFilter: StringFilterInput) {
  config {
    lists {
      users(first: $first, after: $after, filter: { name: $nameFilter }) {
        pageInfo {
          hasNextPage
          endCursor
        }
        items {
          userID
          name
          dateEntered
          profileName
          emailAddress
          pennyWriteOffLimit
          batchOnly
          hasExpense
          invoiceRegisterApproval {
            canApproveStage1
            canApproveStage2
            canApproveStage3
          }
          suggestedPayment {
            canDispute
            canApproveStage1
            canApproveStage2
          }
        }
      }
    }
  }
}`,
            variables: `{
  "first": 50,
  "after": null,
  "nameFilter": { "contains": "John" }
}`,
          },
          searchByProfile: {
            name: 'Search Users by Profile',
            description: 'Search for users by profile name',
            query: `query SearchUsersByProfile($first: Int, $after: String, $profileFilter: StringFilterInput) {
  config {
    lists {
      users(first: $first, after: $after, filter: { profileName: $profileFilter }) {
        pageInfo {
          hasNextPage
          endCursor
        }
        items {
          userID
          name
          dateEntered
          profileName
          emailAddress
          pennyWriteOffLimit
          batchOnly
          hasExpense
          invoiceRegisterApproval {
            canApproveStage1
            canApproveStage2
            canApproveStage3
          }
          suggestedPayment {
            canDispute
            canApproveStage1
            canApproveStage2
          }
        }
      }
    }
  }
}`,
            variables: `{
  "first": 50,
  "after": null,
  "profileFilter": { "eq": "ADMIN" }
}`,
          },
          listWithApprovalRoles: {
            name: 'List Users with Approval Roles',
            description: 'Get users who can approve invoice register at any stage',
            query: `query ListUsersWithApprovalRoles($first: Int, $after: String) {
  config {
    lists {
      users(first: $first, after: $after, filter: { invoiceRegisterApproval: { canApproveStage1: { eq: true } } }) {
        pageInfo {
          hasNextPage
          endCursor
        }
        items {
          userID
          name
          emailAddress
          profileName
          invoiceRegisterApproval {
            canApproveStage1
            canApproveStage2
            canApproveStage3
          }
        }
      }
    }
  }
}`,
            variables: `{
  "first": 100,
  "after": null
}`,
          },
        },
      },
      approvers: {
        name: 'Approvers',
        description: 'Invoice register approvers and groups',
        actions: {
          listGroups: {
            name: 'List Approver Groups',
            description: 'Get invoice register approver groups',
            query: `query ListApproverGroups($first: Int, $after: String, $filter: InvoiceRegisterApproverGroupsFilterInput) {
  config {
    masterRecords {
      invoiceRegisterApproverGroups(first: $first, after: $after, filter: $filter) {
        pageInfo {
          hasNextPage
          endCursor
        }
        items {
          primaryKey
          code
          description
          approvalType
          isStage1Type
          isStage2Type
          isStage3Type
        }
      }
    }
  }
}`,
            variables: `{
  "first": 100,
  "after": null,
  "filter": null
}`,
          },
          listApprovers: {
            name: 'List Approvers',
            description: 'Get invoice register approvers',
            query: `query ListApprovers($first: Int, $after: String, $filter: InvoiceRegisterApproversFilterInput) {
  config {
    masterRecords {
      invoiceRegisterApprovers(first: $first, after: $after, filter: $filter) {
        pageInfo {
          hasNextPage
          endCursor
        }
        items {
          userPrimary
          userId
          userName
          bypassApproval
          approverType1
          approverType2
          approverType3
          isStage1Type
          isStage2Type
          isStage3Type
        }
      }
    }
  }
}`,
            variables: `{
  "first": 100,
  "after": null,
  "filter": null
}`,
          },
        },
      },
      emailTemplates: {
        name: 'Email Templates',
        description: 'Email templates for various document types',
        actions: {
          list: {
            name: 'List Email Templates',
            description: 'Get a list of email templates',
            query: `query ListEmailTemplates($first: Int, $after: String, $filter: EmailTemplateSummaryFilterInput) {
  config {
    lists {
      emailTemplates(first: $first, after: $after, filter: $filter) {
        pageInfo {
          hasNextPage
          endCursor
        }
        items {
          primaryKey
          name
          templateType
          isDefault
        }
      }
    }
  }
}`,
            variables: `{
  "first": 100,
  "after": null,
  "filter": null
}`,
          },
          create: {
            name: 'Create Email Template',
            description: 'Create a new email template',
            query: `mutation CreateEmailTemplate($templates: [EmailTemplateInput!]!) {
  config {
    masterRecords {
      emailTemplates {
        create(templates: $templates) {
          ... on IntEntityIds {
            ids
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "templates": [
    {
      "name": "Custom Invoice Template",
      "templateType": "SalesInvoice",
      "subject": "Invoice {{InvoiceNumber}} from {{CompanyName}}",
      "body": "Please find attached invoice {{InvoiceNumber}}.",
      "format": "Plain",
      "isDefault": false
    }
  ]
}`,
          },
          update: {
            name: 'Update Email Template',
            description: 'Update existing email templates',
            query: `mutation UpdateEmailTemplate($templates: [EmailTemplateInput!]!) {
  config {
    masterRecords {
      emailTemplates {
        update(templates: $templates) {
          ... on IntEntityIds {
            ids
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "templates": [
    {
      "primaryKey": 123,
      "subject": "Updated Subject",
      "body": "Updated email body content"
    }
  ]
}`,
          },
          delete: {
            name: 'Delete Email Template',
            description: 'Delete email templates',
            query: `mutation DeleteEmailTemplate($templateIds: [Int!]!) {
  config {
    masterRecords {
      emailTemplates {
        delete(templateIds: $templateIds) {
          ... on Succeeded {
            succeeded
          }
          ... on FailedValidation {
            errors { message }
            warnings { message }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
}`,
            variables: `{
  "templateIds": [123]
}`,
          },
        },
      },
    },
  },
};

/**
 * Get domain options for dropdown
 */
export function getDomainOptions(): Array<{ value: string; name: string; description: string }> {
  return Object.entries(domains).map(([key, domain]) => ({
    value: key,
    name: domain.name,
    description: domain.description,
  }));
}

/**
 * Get resource options for a specific domain
 */
export function getResourceOptions(domainKey: string): Array<{ value: string; name: string; description: string }> {
  const domain = domains[domainKey];
  if (!domain) return [];

  return Object.entries(domain.resources).map(([key, resource]) => ({
    value: key,
    name: resource.name,
    description: resource.description,
  }));
}

/**
 * Get action options for a specific domain/resource
 */
export function getActionOptions(domainKey: string, resourceKey: string): Array<{ value: string; name: string; description: string }> {
  const domain = domains[domainKey];
  if (!domain) return [];

  const resource = domain.resources[resourceKey];
  if (!resource) return [];

  return Object.entries(resource.actions).map(([key, action]) => ({
    value: key,
    name: action.name,
    description: action.description,
  }));
}

/**
 * Get the template for a specific domain/resource/action combination
 */
export function getTemplate(domainKey: string, resourceKey: string, actionKey: string): QueryTemplate | null {
  const domain = domains[domainKey];
  if (!domain) return null;

  const resource = domain.resources[resourceKey];
  if (!resource) return null;

  return resource.actions[actionKey] || null;
}

/**
 * Get all templates as a flat list (for search)
 */
export function getAllTemplates(): Array<{
  domain: string;
  resource: string;
  action: string;
  template: QueryTemplate;
}> {
  const templates: Array<{
    domain: string;
    resource: string;
    action: string;
    template: QueryTemplate;
  }> = [];

  for (const [domainKey, domain] of Object.entries(domains)) {
    for (const [resourceKey, resource] of Object.entries(domain.resources)) {
      for (const [actionKey, action] of Object.entries(resource.actions)) {
        templates.push({
          domain: domainKey,
          resource: resourceKey,
          action: actionKey,
          template: action,
        });
      }
    }
  }

  return templates;
}
